
import React, { useState } from 'react';
import { 
  TableCell, 
  TableRow 
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronUp, Clock, Trash2 } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ScrollArea } from '@/components/ui/scroll-area';
import { type ErrorLog } from '@/utils/logger';

interface ErrorLogItemProps {
  error: ErrorLog;
  index: number;
  onRemove: (index: number) => void;
}

const ErrorLogItem: React.FC<ErrorLogItemProps> = ({ error, index, onRemove }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const toggleExpand = () => {
    setIsExpanded(prev => !prev);
  };
  
  const formatDate = (date: Date) => {
    if (!(date instanceof Date)) {
      try {
        date = new Date(date);
      } catch (e) {
        return 'Invalid date';
      }
    }
    return date.toLocaleString();
  };

  return (
    <TableRow>
      <TableCell className="font-mono text-xs">
        <div className="flex items-center gap-1">
          <Clock className="h-3 w-3 text-muted-foreground" />
          {formatDate(error.timestamp)}
        </div>
      </TableCell>
      <TableCell>
        {error.component ? (
          <Badge variant="outline">{error.component}</Badge>
        ) : (
          <span className="text-muted-foreground">Unknown</span>
        )}
      </TableCell>
      <TableCell>
        <Collapsible open={isExpanded} onOpenChange={toggleExpand}>
          <div className="flex justify-between items-start">
            <div className="font-medium truncate max-w-md">
              {error.error.message || 'Unknown error'}
            </div>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="p-0 h-6 w-6">
                {isExpanded ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </Button>
            </CollapsibleTrigger>
          </div>
          <CollapsibleContent>
            <div className="mt-2">
              <ScrollArea className="h-[100px] w-full rounded border bg-muted/10 p-2">
                <pre className="text-xs font-mono">
                  {error.error.stack || 'No stack trace available'}
                </pre>
              </ScrollArea>
              
              {error.context && (
                <div className="mt-2">
                  <p className="text-xs font-semibold mb-1">Additional context:</p>
                  <pre className="text-xs bg-muted/10 p-2 rounded">
                    {JSON.stringify(error.context, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </CollapsibleContent>
        </Collapsible>
      </TableCell>
      <TableCell>
        {error.userReported ? (
          <Badge>User Reported</Badge>
        ) : (
          <Badge variant="outline">System</Badge>
        )}
      </TableCell>
      <TableCell>
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={() => onRemove(index)}
        >
          <Trash2 className="h-4 w-4 text-destructive" />
        </Button>
      </TableCell>
    </TableRow>
  );
};

export default ErrorLogItem;
